import { Ban, BellOff, MoreHorizontal, Trash2, VolumeX } from 'lucide-react'
import type { ReactNode } from 'react'

import type { UserBrief } from '@/api/types'
import { Menu, MenuItem } from '@/components/ui/Menu'
import { useT } from '@/i18n'

import { useBlockToggle, useMuteToggle } from '../hooks/useRelations'

interface Props {
  /** 菜单作用的对象 */
  target: UserBrief
  /**
   * 这是不是我自己。由调用方给：帖子用服务端下发的 `viewer.is_author`，
   * 比在这里跟当前用户对 id 更可靠（转发行的作者和内容作者不是同一个人）。
   */
  isSelf?: boolean
  /** 已知的屏蔽 / 静音状态；列表里的帖子拿不到就按 false 处理 */
  blocking?: boolean
  muting?: boolean
  /** 看自己的内容时，菜单换成删除 */
  onDelete?: () => void
  /** 排在最前面的项（如置顶、改回复设置） */
  extra?: (close: () => void) => ReactNode
  /** 排在最后面的项（如举报、管理员处置）。自己和别人都会渲染，由调用方决定内容 */
  trailing?: (close: () => void) => ReactNode
  label?: string
}

/**
 * 帖子与用户页共用的「更多」菜单。
 *
 * 屏蔽要二次确认（会双向取关、清通知，不可撤销地丢掉关系），
 * 静音不确认（随时可撤，对方也无感）。
 */
export function UserActionsMenu({
  target,
  isSelf = false,
  blocking = false,
  muting = false,
  onDelete,
  extra,
  trailing,
  label,
}: Props) {
  const t = useT()
  const block = useBlockToggle()
  const mute = useMuteToggle()

  return (
    <Menu
      trigger={({ toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-label={label ?? t('menu.moreActions')}
          className="rounded-md p-1 text-ink-muted hover:bg-brand-ink/10 hover:text-brand-ink"
        >
          <MoreHorizontal className="size-5" />
        </button>
      )}
    >
      {(close) => (
        <>
          {extra?.(close)}
          {isSelf && onDelete && (
            <MenuItem
              danger
              onClick={() => {
                close()
                onDelete()
              }}
            >
              <Trash2 className="size-4" /> {t('menu.delete')}
            </MenuItem>
          )}
          {!isSelf && (
            <>
              <MenuItem
                onClick={() => {
                  close()
                  mute.mutate({ username: target.username, muting })
                }}
              >
                {muting ? <BellOff className="size-4" /> : <VolumeX className="size-4" />}
                {muting
                  ? t('menu.unmute', { username: target.username })
                  : t('menu.mute', { username: target.username })}
              </MenuItem>
              <MenuItem
                danger
                onClick={() => {
                  close()
                  const confirmed =
                    blocking ||
                    window.confirm(t('menu.confirmBlock', { username: target.username }))
                  if (confirmed) block.mutate({ username: target.username, blocking })
                }}
              >
                <Ban className="size-4" />
                {blocking
                  ? t('menu.unblock', { username: target.username })
                  : t('menu.block', { username: target.username })}
              </MenuItem>
            </>
          )}
          {trailing?.(close)}
        </>
      )}
    </Menu>
  )
}
